import { Box, Stack } from '@mui/material';
import { useLanguageSelector } from '@retail/app/hooks';
import { useAppTFunction } from '@retail/app/i18n';
import { ApprovalToolbar } from '@retail/calculus/components';
import {
  useCalculusConditionSelectionActions,
  useConditionActions,
  useDraftConditions,
} from '@retail/calculus/context';
import { BodyLoader } from '@retail/components';
import { ContainedButton } from '@shared/components';
import { useCallback, useMemo } from 'react';
import { DraftAffectedItemsTable } from './DraftAffectedItemsTable';
import { useMyPriceLists } from '@retail/app/stores/selected-context';

export function ApprovalContainer() {
  const t = useAppTFunction();
  const { language } = useLanguageSelector();
  const { priceListsForDepartment } = useMyPriceLists();

  const { draftConditions, groupedConditions, conditionsLength, draftConditionsIsLoading } =
    useDraftConditions();

  const { removeCondition, activateDraft, isLoadingPostChanges, activateIsLoading } =
    useConditionActions();

  const {
    selectedConditionIds,
    handleSelectConditionId,
    handleSelectAllConditionIdsForSupplierOrAssortment,
    handleSelectAllConditions,
    setSelectedConditionIds,
  } = useCalculusConditionSelectionActions({
    draftConditions,
    groupedConditions,
  });

  const isLoading = useMemo(
    () => isLoadingPostChanges || activateIsLoading,
    [isLoadingPostChanges, activateIsLoading]
  );

  const selectedConditionsCount = useMemo(
    () => selectedConditionIds.length,
    [selectedConditionIds]
  );

  const handleDeleteCondition = useCallback(
    (conditionId: number) => removeCondition([conditionId], 'delete'),
    [removeCondition]
  );
  const submitDeleteConditions = useCallback(() => {
    removeCondition(selectedConditionIds, 'delete');
    setSelectedConditionIds([]);
  }, [removeCondition, selectedConditionIds, setSelectedConditionIds]);

  const submitDraft = useCallback(() => {
    activateDraft(selectedConditionIds);

    setSelectedConditionIds([]);
  }, [setSelectedConditionIds, selectedConditionIds, activateDraft]);

  return (
    <Stack flexGrow={1} overflow="hidden">
      <Stack
        sx={{
          height: '100%',
          overflow: 'auto',
          borderRadius: 'none',
        }}
      >
        <ApprovalToolbar
          isLoading={draftConditionsIsLoading}
          conditionsLength={conditionsLength}
          allSupplierCheckboxSelected={
            selectedConditionsCount === conditionsLength && conditionsLength > 0
          }
          indeterminate={selectedConditionsCount < conditionsLength && !!selectedConditionsCount}
          handleAllSupplierCheckbox={handleSelectAllConditions}
        />

        {draftConditionsIsLoading ? (
          <BodyLoader />
        ) : (
          <DraftAffectedItemsTable
            isLoading={isLoading}
            language={language}
            draftConditions={draftConditions}
            groupedConditions={groupedConditions}
            priceLists={priceListsForDepartment ?? []}
            selectedConditionIds={selectedConditionIds}
            handleDeleteCondition={handleDeleteCondition}
            handleSelectConditionId={handleSelectConditionId}
            handleSelectAllConditionIdsForSupplierOrAssortment={
              handleSelectAllConditionIdsForSupplierOrAssortment
            }
          />
        )}

        <Box display="flex" justifyContent="flex-end" m={2}>
          <Box mr={0.5}>
            <ContainedButton
              color="warning"
              size="small"
              disabled={!selectedConditionsCount}
              onClick={submitDeleteConditions}
              loading={isLoading}
            >
              {selectedConditionsCount === 1
                ? t('calculus.actions.disapproveCondition', { count: selectedConditionsCount })
                : t('calculus.actions.disapproveConditions', { count: selectedConditionsCount })}
            </ContainedButton>
          </Box>

          <ContainedButton
            onClick={submitDraft}
            size="small"
            disabled={!selectedConditionsCount}
            loading={isLoading}
          >
            {selectedConditionsCount === 1
              ? t('calculus.actions.approveCondition', { count: selectedConditionsCount })
              : t('calculus.actions.approveConditions', { count: selectedConditionsCount })}
          </ContainedButton>
        </Box>
      </Stack>
    </Stack>
  );
}
